import { defineStore } from 'pinia'
import { SmartSearchConfigInterface } from '@/core/types'
import { useUrlStore } from '@/store/SerpPage/UrlStore'
import { SmartSearchConfig } from '@/main'

import { SanitizeValue } from '@/core/utils'

interface WindowWithConfig extends Window {
  SmartSearchInstanceConfig?: SmartSearchConfigInterface
}

function resolveSmartSearchConfig (): SmartSearchConfigInterface | null {
  if (SmartSearchConfig?.value) {
    return SmartSearchConfig.value
  }

  if (typeof window !== 'undefined') {
    const globalConfig = (window as WindowWithConfig).SmartSearchInstanceConfig
    if (globalConfig) {
      return globalConfig
    }
  }

  return null
}

function shouldUseHashBeforeSearchParams (): boolean {
  const config = resolveSmartSearchConfig()
  return config?.appConfiguration?.useHashBeforeSearchParams ?? false
}

export const useSearchQueryStore = defineStore('searchQueryStore', {
  state: () => ({
    initialized: false,
    searchQuery: ''
  }),
  getters: {
    getIsHashBeforeSearchParams () {
      return shouldUseHashBeforeSearchParams()
    },
    hasQuery (state) {
      return state.searchQuery !== ''
    },
    getSearchQuery (state) {
      if (!state.initialized) {
        useSearchQueryStore().initSearchQuery()
      }
      return SanitizeValue(state.searchQuery)
    },
    getSerpRedirectUrl (): string {
      const config = resolveSmartSearchConfig()
      const executionPath = process.env.NODE_ENV === 'production'
        ? process.env.VUE_APP_PUBLIC_PATH
        : ''
      const redirectPath = config?.serpRedirectUrl ?? config?.appConfiguration?.redirectionUrl ?? '/'
      const shouldUseHash = this.getIsHashBeforeSearchParams

      if (shouldUseHash) {
        return encodeURI(`${executionPath}${redirectPath}#?q=${this.getSearchQuery}`)
      }

      return encodeURI(`${executionPath}${redirectPath}?q=${this.getSearchQuery}`)
    }
  },
  actions: {
    getSearchParams () {
      return useUrlStore().decomposeUrl().searchParams
    },
    updateQuery (newQuery: string) {
      this.searchQuery = SanitizeValue(newQuery)
    },
    clearQuery () {
      this.searchQuery = ''
    },
    initSearchQuery () {
      const urlParams = this.getSearchParams()
      const urlQuery = SanitizeValue(urlParams.get('q') ?? '')
      if (!this.searchQuery || this.searchQuery.trim().length === 0) {
        this.searchQuery = urlQuery
      }
      this.initialized = true
    }
  }
})
